import React, { FC, useEffect, useState } from 'react';
import PrequalProgressBarStep, { PrequalProgressBarStepProps } from './PrequalProgressBarStep';
import styles from './PrequalProgressBar.module.scss';
import {
  PREQUAL_PROGRESS_CONFIG,
  getCurrentMilestoneIndex,
  LendAPIProgressConfig,
} from '../../prequalProgressConfig';

interface PrequalProgressBarProps {
  currentTab: string;
  lastKnownTab?: string;
  config?: LendAPIProgressConfig<string>;
}

const PrequalProgressBar: FC<PrequalProgressBarProps> = ({
  currentTab,
  lastKnownTab,
  config = PREQUAL_PROGRESS_CONFIG as LendAPIProgressConfig<string>,
}) => {
  const [steps, setSteps] = useState<PrequalProgressBarStepProps[]>([]);

  useEffect(() => {
    const newSteps: PrequalProgressBarStepProps[] = [];
    const currentMilestoneIndex = getCurrentMilestoneIndex(currentTab, config, lastKnownTab);

    if (currentMilestoneIndex === -1) {
      setSteps([]);
      return;
    }

    let visibleIndex = 0;
    config.milestones.forEach((milestone, milestoneIndex) => {
      const shouldShowMilestone = !(milestone.shouldHide?.(currentTab) ?? false);
      if (shouldShowMilestone) {
        const isCompleted = milestoneIndex < currentMilestoneIndex;
        const isCurrent = milestoneIndex === currentMilestoneIndex;
        const isStartOfGroup = config.tabLookup.get(currentTab)?.isStartOfGroup ?? false;

        let status: PrequalProgressBarStepProps['status'];
        if (isCompleted) {
          status = 'done';
        } else if (isCurrent) {
          status = 'current';
        } else {
          status = 'pending';
        }

        let position: PrequalProgressBarStepProps['position'];
        const isFirst = visibleIndex === 0;
        if (isFirst) {
          position = 'start';
        } else {
          position = 'middle';
        }

        const shouldShowBarToTheLeft = !isFirst;
        const isActive = isCompleted || isCurrent;
        const shouldShowInnerCircle = isCompleted || isCurrent;
        const isLeftBarActive = isActive;
        const isRightBarActive = isCompleted || (isCurrent && !isStartOfGroup);
        const isLabelActive = isActive;

        newSteps.push({
          isLabelActive,
          isLeftBarActive,
          isRightBarActive,
          label: milestone.name,
          position,
          shouldShowBarToTheLeft,
          shouldShowBarToTheRight: true,
          shouldShowInnerCircle,
          status,
        });

        visibleIndex++;
      }
    });

    if (newSteps.length > 0) {
      const lastStep = newSteps[newSteps.length - 1];
      lastStep.position = 'end';
      lastStep.shouldShowBarToTheRight = false;
    }

    setSteps(newSteps);
  }, [currentTab, lastKnownTab, config]);

  if (steps.length === 0) {
    return null;
  }

  return (
    <div className={styles.prequalProgressBar}>
      <div className={styles.prequalProgressBar__stepsContainer}>
        {steps.map((step) => (
          <PrequalProgressBarStep key={step.label} {...step} />
        ))}
      </div>
    </div>
  );
};

export default PrequalProgressBar;
